import { useState } from 'react';
import { FaTimes, FaCheck } from 'react-icons/fa';
import '../styles/ItemModal.css';

const ItemModal = ({ item, onClose, onAddToCart }) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [showError, setShowError] = useState(false);

  if (!item) {
    return null;
  }

  const handleAddToCart = () => {
    // Check if size and color are required and selected
    const needsSize = item.sizes && item.sizes.length > 0 && !item.sizes.includes('One Size');
    const needsColor = item.colors && item.colors.length > 0;

    if ((needsSize && !selectedSize) || (needsColor && !selectedColor)) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    onAddToCart({
      ...item,
      size: selectedSize || 'One Size',
      color: selectedColor || 'Default',
    });
    onClose();
  };

  // Color mapping for better visual representation
  const getColorStyle = color => {
    const colorMap = {
      White: '#FFFFFF',
      Black: '#000000',
      Gray: '#808080',
      Grey: '#808080',
      Navy: '#000080',
      Blue: '#0066CC',
      Red: '#DC2626',
      Pink: '#EC4899',
      Yellow: '#EAB308',
      Green: '#16A34A',
      Brown: '#A52A2A',
      Beige: '#F5F5DC',
      Purple: '#7C3AED',
    };

    return {
      backgroundColor: colorMap[color] || color.toLowerCase(),
      border:
        color === 'White'
          ? '2px solid #ddd'
          : selectedColor === color
            ? '3px solid #000'
            : '2px solid transparent',
    };
  };

  return (
    <div className='item-modal-overlay' onClick={onClose}>
      <div className='item-modal' onClick={e => e.stopPropagation()}>
        <div className='item-modal-header'>
          <h2 className='item-modal-title'>{item.name}</h2>
          <button className='item-modal-close-btn' onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className='item-modal-content'>
          <div className='item-modal-grid'>
            <div className='modal-item-card'>
              <div className='modal-item-image'>
                <img
                  src={item.image_url || '/assets/placeholder.jpg'}
                  alt={item.name}
                  onError={e => {
                    e.target.src = '/assets/placeholder.jpg';
                  }}
                />
              </div>
              <div className='modal-item-info'>
                <h3 className='modal-item-name'>{item.name}</h3>
                <p className='modal-item-price'>${item.price?.toFixed(2) || '0.00'}</p>
                <p className='modal-item-category'>{item.category}</p>
              </div>
            </div>
          </div>

          <div className='modal-details'>
            <p className='description'>
              {item.description || 'A versatile and elegant piece perfect for any occasion.'}
            </p>

            {/* Size Selection */}
            {item.sizes && item.sizes.length > 0 && (
              <div className='selection-section'>
                <h3 className='selection-title'>
                  Size: {selectedSize && <span className='selected-value'>{selectedSize}</span>}
                </h3>
                <div className='size-options'>
                  {item.sizes.map(size => (
                    <button
                      key={size}
                      className={`size-button ${selectedSize === size ? 'selected' : ''}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                      {selectedSize === size && <FaCheck className='check-icon' />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {item.colors && item.colors.length > 0 && (
              <div className='selection-section'>
                <h3 className='selection-title'>
                  Color: {selectedColor && <span className='selected-value'>{selectedColor}</span>}
                </h3>
                <div className='color-options'>
                  {item.colors.map(color => (
                    <button
                      key={color}
                      className={`color-button ${selectedColor === color ? 'selected' : ''}`}
                      style={getColorStyle(color)}
                      onClick={() => setSelectedColor(color)}
                      title={color}
                    >
                      {selectedColor === color && (
                        <FaCheck
                          className='color-check-icon'
                          style={{
                            color:
                              color === 'White' || color === 'Yellow' || color === 'Beige'
                                ? '#000'
                                : '#fff',
                          }}
                        />
                      )}
                      <span className='sr-only'>{color}</span>
                    </button>
                  ))}
                </div>
                <div className='color-names'>
                  {item.colors.map(color => (
                    <span
                      key={`name-${color}`}
                      className={`color-name ${selectedColor === color ? 'active' : ''}`}
                      onClick={() => setSelectedColor(color)}
                    >
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Error Message */}
            {showError && (
              <div className='error-message'>
                <p>
                  Please select{' '}
                  {!selectedSize && item.sizes && !item.sizes.includes('One Size') ? 'size' : ''}
                  {!selectedSize &&
                  !selectedColor &&
                  item.sizes &&
                  !item.sizes.includes('One Size') &&
                  item.colors
                    ? ' and '
                    : ''}
                  {!selectedColor && item.colors ? 'color' : ''} before adding to cart.
                </p>
              </div>
            )}

            <div className='action-buttons'>
              <button
                className={`add-to-cart ${showError ? 'error' : ''}`}
                onClick={handleAddToCart}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemModal;
